// CalendarNewPage — форма создания нового события календаря.
// После успешного create → редирект на /calendar/events/{id}.
// R32 / U-8: query-параметр ?date=yyyy-MM-dd префиллит даты начала/окончания
// (переход с DayEventsPage по кнопке «Создать событие»).
// B1: departmentId — обязательный Select формы (backend @NotNull); fake-UUID убран.
// U-30: allDay:false в payload (BE @NotNull) + prefill через toISOString (Instant с Z)
// + Alert через extractApiErrorMessage (без маскировки причины).
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Box, Alert } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import {
  CalendarEventForm,
  type CalendarEventFormValues,
} from './components/CalendarEventForm';
import { calendarApi } from '@/api/calendar';
import { useAuth } from '@/auth/AuthContext';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Формат query-параметра даты (yyyy-MM-dd). */
const DATE_PARAM_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export function CalendarNewPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const prefillDate = dateParam && DATE_PARAM_PATTERN.test(dateParam) ? dateParam : null;

  // B1: дефолт — первый департамент текущего пользователя; иначе пусто + validation error.
  const formDefaults: Partial<CalendarEventFormValues> = {
    departmentId: currentUser?.departments[0]?.id ?? '',
  };
  if (prefillDate) {
    // U-30: ISO Instant с timezone (Z) — без Z/offset backend Instant не парсит (Jackson).
    // new Date('2026-09-05T10:00:00') по ES-спецификации — локальное время: слот остаётся «дневным».
    formDefaults.startAt = new Date(`${prefillDate}T10:00:00`).toISOString();
    formDefaults.endAt = new Date(`${prefillDate}T11:00:00`).toISOString();
  }

  // U-24: ref-флаг закрывает окно «двойной клик/Enter до ре-рендера isPending=true» —
  // useMutation отдаёт snapshot (useSyncExternalStore), в замыкании isPending не виден
  // до ререндера; кнопка disabled — вторая линия защиты.
  const submittingRef = useRef(false);
  const mutation = useMutation({
    mutationFn: (values: CalendarEventFormValues) =>
      calendarApi.create({
        title: values.title,
        description: values.description || null,
        location: values.location || null,
        startAt: values.startAt,
        endAt: values.endAt,
        type: values.type,
        // S5 (V13): allDay из формы (Switch «Весь день»), не жёсткое false — BE @NotNull.
        allDay: values.allDay,
        // B1: департамент — явное поле формы (обязательное), не fake-UUID.
        departmentId: values.departmentId,
      }),
    onSuccess: (event) => {
      // U-29: убрать no-op ['calendar'] — списки живут под ['calendar-events']/['calendar-day'].
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      void queryClient.invalidateQueries({ queryKey: ['calendar-day'] });
      navigate(`/calendar/events/${event.id}`);
    },
    // U-24: после завершения мутации ref сбрасывается — форма снова submit-доступна.
    onSettled: () => {
      submittingRef.current = false;
    },
  });

  return (
    <Box>
      <PageHeader title="Новое событие" subtitle="Заполните обязательные поля и сохраните" />
      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {extractApiErrorMessage(mutation.error, t('calendar.createError'))}
        </Alert>
      )}
      <CalendarEventForm
        // R32 / U-8: prefill даты из ?date= (10:00–11:00 — разумный дефолт слота) + B1 департамент.
        defaultValues={formDefaults}
        // U-24: guard — защита окна «двойной клик/Enter до ре-рендера isPending=true».
        onSubmit={(values) => {
          if (submittingRef.current || mutation.isPending) return;
          submittingRef.current = true;
          mutation.mutate(values);
        }}
        submitting={mutation.isPending}
        submitLabel="Создать событие"
      />
    </Box>
  );
}